/**
 * REALTY-LIFECYCLE-F1 — owner dashboard filter vocabulary and helpers.
 * Pure data/helpers only; no API or Redux.
 */

import {
  buildEditLocationHref,
  needsLocationUpgrade,
} from "../../geo/lib/locationGeo";
import {
  getLifecycleConfirmation,
  getLifecycleNotice,
  getOwnerLifecycleActions,
  getStatusSuccessMessage,
  isModerationLocked,
} from "../../realty/lib/propertyLifecycle";
import {
  normalizeProfileDealType,
  orderingFromProfileSort,
} from "./profilePropertyTypes";

export const OWNER_STATUS_FILTERS = [
  { id: "all", label: "Усі", statusParam: null },
  { id: "draft", label: "Чернетки", statusParam: "draft" },
  { id: "active", label: "Активні", statusParam: "active" },
  { id: "reserved", label: "Зарезервовані", statusParam: "reserved" },
  { id: "sold", label: "Завершені", statusParam: "sold" },
  { id: "archived", label: "В архіві", statusParam: "archived" },
];

export const OWNER_EMPTY_STATES = {
  all: {
    title: "У вас ще немає оголошень",
    description: "Створені вами оголошення з'являться тут.",
    showCreate: true,
  },
  draft: {
    title: "Немає чернеток",
    description: "Чернетки оголошень з'являться тут.",
    showCreate: true,
  },
  active: {
    title: "Немає активних оголошень",
    description: "Опубліковані оголошення з'являться тут.",
    showCreate: true,
  },
  reserved: {
    title: "Немає зарезервованих оголошень",
    description: "Зарезервовані оголошення з'являться тут.",
    showCreate: false,
  },
  sold: {
    title: "Немає завершених оголошень",
    description: "Завершені угоди з'являться тут.",
    showCreate: false,
  },
  archived: {
    title: "В архіві немає оголошень",
    description: "Архівні оголошення з'являться тут.",
    showCreate: false,
  },
};

const VALID_FILTER_IDS = new Set(OWNER_STATUS_FILTERS.map((item) => item.id));

export function normalizeOwnerStatusFilter(value) {
  return VALID_FILTER_IDS.has(value) ? value : "all";
}

export function ownerListParams({
  statusFilter = "all",
  typeSet,
  sort,
  dealType,
  page = 1,
  pageSize = 20,
}) {
  const filterDef = OWNER_STATUS_FILTERS.find((item) => item.id === statusFilter)
    || OWNER_STATUS_FILTERS[0];
  const params = {
    created_by: "me",
    page,
    page_size: pageSize,
  };
  if (filterDef.statusParam) {
    params.status = filterDef.statusParam;
  }
  const types = Array.from(typeSet || []);
  if (types.length > 0) {
    params.property_type = types;
  }
  const normalizedDeal = normalizeProfileDealType(dealType);
  if (normalizedDeal) {
    params.deal_type = normalizedDeal;
  }
  params.ordering = orderingFromProfileSort(sort);
  return params;
}

export function buildOwnerEditHref(property) {
  const id = property.id;
  if (needsLocationUpgrade(property.location)) {
    return buildEditLocationHref(id);
  }
  return `/realty/${id}/edit`;
}

/**
 * Gear-menu items for owned Property cards on /profile.
 * Lifecycle targets come from propertyLifecycle.js only.
 */
export function buildOwnerCardMenuItems(property, handlers = {}) {
  const {
    onShareCopy,
    copiedId,
    onLifecycleAction,
    isActionPending,
  } = handlers;
  const status = property.status || "draft";
  const lifecycleActions = getOwnerLifecycleActions(property, { isOwner: true });
  const statusActions = lifecycleActions.filter((action) => action.id !== "delete");
  const deleteAction = lifecycleActions.find((action) => action.id === "delete");
  const pending = Boolean(isActionPending && isActionPending(property.id));

  const items = [];

  if (onShareCopy) {
    items.push({
      id: "share",
      label: copiedId === property.id ? "Посилання скопійовано" : "Поширити",
      icon: "fa-solid fa-share-nodes",
      kind: "button",
      dismiss: false,
      onClick: () => onShareCopy(property),
    });
  }

  items.push({
    id: "edit",
    label: status === "draft" ? "Продовжити" : "Редагувати",
    icon: status === "draft" ? "fa-solid fa-play" : "fa-solid fa-pen-to-square",
    kind: "link",
    href: buildOwnerEditHref(property),
  });

  for (const action of statusActions) {
    items.push({
      id: action.id,
      label: action.label,
      icon: action.icon,
      kind: "button",
      danger: Boolean(action.destructive),
      disabled: pending,
      onClick: () => onLifecycleAction(property, action),
    });
  }

  if (deleteAction) {
    items.push({
      id: deleteAction.id,
      label: deleteAction.label,
      icon: deleteAction.icon,
      kind: "button",
      danger: true,
      disabled: pending,
      onClick: () => onLifecycleAction(property, deleteAction),
    });
  }

  return items;
}

export function getOwnerCardModerationNotice(property) {
  if (!property) return null;
  if (property.status === "archived" && isModerationLocked(property)) {
    return getLifecycleNotice(property);
  }
  return null;
}

export function lifecycleSuccessMessage(property, targetStatus) {
  return getStatusSuccessMessage(targetStatus, {
    dealType: property.deal_type,
    fromStatus: property.status,
  });
}

export function lifecycleConfirmMeta(actionId, property) {
  return getLifecycleConfirmation(actionId, property);
}
